/**
 * Owner: Masa Bayu
 * File: batch04-evidence.ts
 * Date:Time: 2026-09-20 00:15
 * Description: Batch 4 evidence packs — SEA no-space · READY · not Supported.
 */
import { QX_BATCH_04_EVIDENCE_AREAS, QX_BATCH_04_MODEL_VERSION, QX_GL_CERT_GLOBAL_BATCH_04_ID, } from './batch04-codes';
import { fixturesForBatch04Family, listBatch04FixtureIds, QX_BATCH_04_KHMER_SAMPLE_LANGUAGES, QX_BATCH_04_LAO_SAMPLE_LANGUAGES, QX_BATCH_04_MYANMAR_SAMPLE_LANGUAGES, QX_BATCH_04_TEXT_SAMPLES, QX_BATCH_04_THAI_SAMPLE_LANGUAGES, } from './batch04-fixtures';
import { createQxAccessibilityLanguageSemantics } from './a11y-semantics';
import { allEvidenceTrue, evaluateEvidencePath, } from './certification-evidence';
import { createQxFamilyCertificationRow, supportClaimForRow, } from './certification-row';
import { getCoverageScriptDirectionRow } from './coverage-script-matrix';
import { resolveDirectionHint } from './direction-resolve';
import { QxLanguageError } from './errors';
import { createQxLanguageIdentity } from './identity';
import { assertQxpLanguageMetadataFromQxwOnly, createQxLanguageMetadataBinding, preservePublicationLanguageMetadata, } from './metadata-placement';
import { applyOverrideWithoutChangingContentId, createQxMultilingualContentModel, } from './multilingual-content';
import { roundTripQxMultilingualContentModel, serializeQxMultilingualContentModel, } from './multilingual-serialize';
import { parseQxPublicationLanguageMetadata, serializeQxPublicationLanguageMetadata, } from './metadata-serialize';
import { createQxPublicationLanguageMetadata } from './publication-metadata';
import { resolveLanguageScriptRelation } from './script-relation';
import { createQxTextProcessingProfile, prepareSearchText, } from './search-prep';
import { selectSegmentStrategy } from './text-segment';
import { normalizeTextForSearch } from './text-normalize';
import { resolveTypographyForScript } from './typography-resolve';
import { resolveWritingModeHint } from './writing-mode-resolve';
const FAMILY_CFG = Object.freeze({
    thai: Object.freeze({
        scriptId: 'Thai',
        primary: 'th',
        samples: QX_BATCH_04_THAI_SAMPLE_LANGUAGES,
        sampleText: QX_BATCH_04_TEXT_SAMPLES.th,
        contentId: 'doc-thai-batch04-01',
        analyzer: 'thai_script',
    }),
    lao: Object.freeze({
        scriptId: 'Laoo',
        primary: 'lo',
        samples: QX_BATCH_04_LAO_SAMPLE_LANGUAGES,
        sampleText: QX_BATCH_04_TEXT_SAMPLES.lo,
        contentId: 'doc-lao-batch04-01',
        analyzer: 'lao_script',
    }),
    khmer: Object.freeze({
        scriptId: 'Khmr',
        primary: 'km',
        samples: QX_BATCH_04_KHMER_SAMPLE_LANGUAGES,
        sampleText: QX_BATCH_04_TEXT_SAMPLES.km,
        contentId: 'doc-khmer-batch04-01',
        analyzer: 'khmer_script',
    }),
    myanmar: Object.freeze({
        scriptId: 'Mymr',
        primary: 'my',
        samples: QX_BATCH_04_MYANMAR_SAMPLE_LANGUAGES,
        sampleText: QX_BATCH_04_TEXT_SAMPLES.my,
        contentId: 'doc-myanmar-batch04-01',
        analyzer: 'myanmar_script',
    }),
});
function allAreasTrue(areas) {
    for (const key of QX_BATCH_04_EVIDENCE_AREAS) {
        if (!areas[key]) {
            return false;
        }
    }
    return true;
}
function whitespaceOverrideRejected(languageId) {
    try {
        selectSegmentStrategy(languageId, 'whitespace');
        return false;
    }
    catch {
        return true;
    }
}
/**
 * Build one Batch 4 SEA family evidence pack.
 * Proves no whitespace word-boundary default for no-space scripts.
 */
export function buildBatch04FamilyEvidenceBundle(familyId) {
    const cfg = FAMILY_CFG[familyId];
    const areas = {};
    const fixtures = fixturesForBatch04Family(familyId);
    const matrixRow = getCoverageScriptDirectionRow(familyId);
    const primary = createQxLanguageIdentity({
        languageId: cfg.primary,
        plane: 'publication',
    });
    areas.identity =
        primary.languageId === cfg.primary &&
            cfg.samples.includes(cfg.primary);
    const scriptRel = resolveLanguageScriptRelation(cfg.primary);
    areas.script =
        scriptRel.resolvedScriptId === cfg.scriptId &&
            matrixRow.primaryScriptIds.includes(cfg.scriptId) &&
            matrixRow.complexShaping === true &&
            matrixRow.whitespaceTokenizationSafe === false;
    const dir = resolveDirectionHint(cfg.primary);
    const dirOverride = resolveDirectionHint(cfg.primary, 'ltr');
    areas.direction =
        dir.hintedDirection === 'ltr' && dirOverride.override === 'ltr';
    const content = createQxMultilingualContentModel({
        contentId: cfg.contentId,
        primaryLanguageId: cfg.primary,
        contentLanguages: [...cfg.samples],
        overrides: [],
    });
    areas.content = content.primaryLanguageId === cfg.primary;
    const pubMeta = createQxPublicationLanguageMetadata({
        primaryLanguageId: cfg.primary,
        contentLanguages: [...cfg.samples],
    });
    const qxwBinding = createQxLanguageMetadataBinding({
        placement: 'qxw_document_root',
        primaryLanguageId: pubMeta.primaryLanguageId,
        defaultScriptId: pubMeta.defaultScriptId,
        defaultDirection: pubMeta.defaultDirection,
        defaultWritingMode: pubMeta.defaultWritingMode,
        contentLanguages: pubMeta.contentLanguages,
    });
    const preserved = preservePublicationLanguageMetadata(qxwBinding.metadata);
    areas.qxw =
        preserved.primaryLanguageId === cfg.primary &&
            preserved.defaultScriptId === cfg.scriptId &&
            preserved.defaultDirection === 'ltr';
    let qxpOk = false;
    try {
        assertQxpLanguageMetadataFromQxwOnly(true);
        const qxp = createQxLanguageMetadataBinding({
            placement: 'qxp_artifact',
            primaryLanguageId: cfg.primary,
            contentLanguages: [...cfg.samples],
        });
        qxpOk =
            qxp.preserveThroughPublish === true &&
                qxp.metadata.defaultDirection === 'ltr';
    }
    catch {
        qxpOk = false;
    }
    areas.qxp = qxpOk;
    const typo = resolveTypographyForScript({
        role: 'body',
        languageId: cfg.primary,
    });
    const heading = resolveTypographyForScript({
        role: 'heading',
        languageId: cfg.primary,
    });
    areas.reader =
        typo.scriptId === cfg.scriptId &&
            heading.scriptId === cfg.scriptId &&
            typo.shapingRequired === true &&
            typo.fallbackChain.length > 0;
    const profile = createQxTextProcessingProfile({ languageId: cfg.primary });
    const prep = prepareSearchText(profile, cfg.sampleText);
    const nfc = normalizeTextForSearch(QX_BATCH_04_TEXT_SAMPLES.composed, 'NFC');
    areas.search =
        profile.analyzerFamily === cfg.analyzer &&
            profile.segmentStrategy === 'script_aware' &&
            prep.segmentation.strategy === 'script_aware' &&
            prep.segmentation.engineRequired === true &&
            matrixRow.whitespaceTokenizationSafe === false &&
            whitespaceOverrideRejected(cfg.primary) &&
            nfc.normalized === 'café' &&
            nfc.original === QX_BATCH_04_TEXT_SAMPLES.composed;
    const wm = resolveWritingModeHint(cfg.primary);
    areas.navigation =
        wm.hintedWritingMode === 'horizontal-tb' && heading.role === 'heading';
    areas.annotation = nfc.original !== nfc.normalized && nfc.original.length > 0;
    const a11y = createQxAccessibilityLanguageSemantics({
        contentLanguageId: cfg.primary,
        uiAnnouncementLanguageId: 'en',
        spans: [],
    });
    areas.accessibility =
        a11y.contentLangTag === cfg.primary &&
            a11y.direction === 'ltr' &&
            a11y.readingOrderPreserved === true;
    const metaRt = parseQxPublicationLanguageMetadata(serializeQxPublicationLanguageMetadata(pubMeta));
    const contentRt = roundTripQxMultilingualContentModel(content);
    const json = serializeQxMultilingualContentModel(content);
    areas.serialization =
        metaRt.primaryLanguageId === cfg.primary &&
            metaRt.defaultDirection === 'ltr' &&
            contentRt.contentId === cfg.contentId &&
            typeof json.modelVersion === 'string';
    const next = applyOverrideWithoutChangingContentId(content, {
        scope: 'block',
        spanId: `note-${cfg.primary}`,
        languageId: cfg.primary,
    }, cfg.contentId);
    areas.regression =
        next.contentId === content.contentId && next.overrides.length === 1;
    const provenance = fixtures.map((f) => Object.freeze({ fixtureId: f.id, area: f.area }));
    const areaSet = new Set(QX_BATCH_04_EVIDENCE_AREAS);
    areas.provenance =
        provenance.length === QX_BATCH_04_EVIDENCE_AREAS.length &&
            provenance.every((p) => areaSet.has(p.area));
    const frozenAreas = Object.freeze({ ...areas });
    const pathEvidence = allEvidenceTrue();
    const pathEval = evaluateEvidencePath(pathEvidence);
    const areasPass = allAreasTrue(frozenAreas);
    const row = createQxFamilyCertificationRow({
        familyId,
        scriptIds: [cfg.scriptId],
        sampleLanguageIds: [...cfg.samples],
        directions: ['ltr'],
        level: 'experimental',
        evidence: pathEvidence,
        evidenceRefs: listBatch04FixtureIds(familyId),
        gaps: ['awaiting_owner_accept_for_certified', 'batch_open_not_supported'],
        ownerAcceptedAt: null,
    });
    const claimStatus = supportClaimForRow(row);
    if (claimStatus === 'supported') {
        throw new QxLanguageError('INVALID_INPUT', `${familyId} must not claim Supported before Owner Accept`);
    }
    const ready = areasPass && pathEval.status === 'pass';
    return Object.freeze({
        modelVersion: QX_BATCH_04_MODEL_VERSION,
        batchId: QX_GL_CERT_GLOBAL_BATCH_04_ID,
        familyId,
        scriptId: cfg.scriptId,
        areas: frozenAreas,
        pathEvidence,
        pathStatus: pathEval.status,
        fixtures: listBatch04FixtureIds(familyId),
        provenance: Object.freeze(provenance),
        row,
        supportedClaimAllowed: false,
        claimStatus,
        readyForOwnerAccept: ready,
        automatedTestsPass: ready,
        manualQaChecklistPackaged: true,
        a11ySpotCheckPass: areas.accessibility,
        notes: ready
            ? `${familyId} Batch 4 SEA no-space evidence READY — not Supported`
            : `${familyId} Batch 4 evidence incomplete`,
    });
}
export function buildBatch04EvidenceReport() {
    const thai = buildBatch04FamilyEvidenceBundle('thai');
    const lao = buildBatch04FamilyEvidenceBundle('lao');
    const khmer = buildBatch04FamilyEvidenceBundle('khmer');
    const myanmar = buildBatch04FamilyEvidenceBundle('myanmar');
    const ready = thai.readyForOwnerAccept &&
        lao.readyForOwnerAccept &&
        khmer.readyForOwnerAccept &&
        myanmar.readyForOwnerAccept;
    return Object.freeze({
        batchId: QX_GL_CERT_GLOBAL_BATCH_04_ID,
        modelVersion: QX_BATCH_04_MODEL_VERSION,
        thai,
        lao,
        khmer,
        myanmar,
        readyForOwnerAccept: ready,
        batchOpenImpliesSupported: false,
        supportedFamilyIdsStill: Object.freeze([
            'latin-core',
            'arabic',
            'hebrew',
            'cyrillic',
            'greek',
            'chinese',
            'japanese',
            'korean',
        ]),
    });
}
//# sourceMappingURL=batch04-evidence.js.map